import React from 'react';
import {AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer} from 'recharts';

export const Card: React.FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
    <div className={`rounded-xl border border-mld-panel-border bg-mld-menu-background ${className}`}>{children}</div>
);

export const Stat: React.FC<{value: string; label: string}> = ({value, label}) => (
    <div>
        <div className="text-[28px] leading-tight font-medium text-mld-text-primary">{value}</div>
        <div className="-mt-0.5 text-[12px] text-mld-muted-text">{label}</div>
    </div>
);

export const SectionTitle: React.FC<{children: React.ReactNode; badge?: string; className?: string}> = ({
    children,
    badge,
    className = '',
}) => (
    <div className={`flex items-center gap-2.5 ${className}`}>
        <h2 className="text-[24px] text-mld-muted-text">{children}</h2>
        {badge && (
            <span className="rounded-md bg-mld-primary px-5 py-1 text-[13px] font-medium text-white">{badge}</span>
        )}
    </div>
);

export const DonutChart: React.FC<{
    pct: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
    trackColor?: string;
    children?: React.ReactNode;
}> = ({pct, size = 110, strokeWidth = 10, color = 'rgb(var(--mld-primary))', trackColor = 'rgb(var(--mld-panel-border))', children}) => {
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (Math.max(0, Math.min(100, pct)) / 100) * circ;
    return (
        <div className="relative" style={{width: size, height: size}}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circ}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
        </div>
    );
};

export const MiniAreaChart: React.FC<{
    data: Array<{day: string; value: number}>;
    color?: string;
    height?: number;
    yFormatter?: (v: number) => string;
}> = ({data, color = 'rgb(var(--mld-info))', height = 110, yFormatter}) => {
    const padded = [
        {day: '', value: data[0]?.value ?? 0},
        ...data,
        {day: ' ', value: data[data.length - 1]?.value ?? 0},
    ];
    const customDot = (props: {cx?: number; cy?: number; index?: number}) => {
        const {cx, cy, index} = props;
        if (index === 0 || index === padded.length - 1) return <g />;
        return <circle cx={cx} cy={cy} r={3.5} fill={color} stroke={color} strokeWidth={0} />;
    };
    // Gradient defs need a DOM-safe id; the previous `color.replace('#', '')`
    // assumed a #RRGGBB literal and breaks for `rgb(var(--...))` tokens.
    const gradientId = `fill-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={padded} margin={{top: 5, right: 0, bottom: 0, left: -20}}>
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="rgb(var(--mld-panel-border))" strokeWidth={0.5} />
                <XAxis
                    dataKey="day"
                    tick={{fontSize: 10, fill: 'rgb(var(--mld-muted-text))'}}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    padding={{left: 0, right: 0}}
                />
                <YAxis
                    tick={{fontSize: 10, fill: 'rgb(var(--mld-muted-text))'}}
                    axisLine={false}
                    tickLine={false}
                    width={45}
                    tickFormatter={yFormatter}
                />
                <Tooltip contentStyle={{fontSize: 11, borderRadius: 8, border: '1px solid rgb(var(--mld-panel-border))', padding: '6px 10px'}} />
                <Area
                    type="natural"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={1.5}
                    fill={`url(#${gradientId})`}
                    dot={customDot}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export const HorizontalBar: React.FC<{pct: number; color: string; label: string; detail: string}> = ({
    pct,
    color,
    label,
    detail,
}) => (
    <div>
        <div className="mb-0.5 text-[12px] text-mld-muted-text">{label}</div>
        <div className="flex items-center gap-3">
            <div className="mr-[6px] h-[28px] flex-1 overflow-hidden rounded-full bg-mld-panel-bg">
                <div
                    className="h-full rounded-full transition-all"
                    style={{width: `${Math.max(0, Math.min(100, pct * 2))}%`, backgroundColor: color}}
                />
            </div>
            <div className="w-[100px] text-right text-[18px] text-mld-muted-text">{detail}</div>
        </div>
    </div>
);

export const DataTable: React.FC<{headers: string[]; rows: string[][]}> = ({headers, rows}) => (
    // Surface tokens for header + both row variants so text auto-derives
    // in lockstep with the background on light AND dark tenant themes.
    // Mixing a static bg-white with theme-following text-* was the
    // invisibility bug — header navy, even-row near-white, odd-row alt
    // all need to come from the same theming layer as the text.
    <div className="overflow-hidden">
        <table className="w-full text-xs">
            <thead>
                <tr className="bg-mld-primary">
                    {headers.map((h, i) => (
                        <th key={i} className="px-4 py-2.5 text-left font-semibold text-white">
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-mld-menu-background' : 'bg-mld-background'}>
                        {row.map((cell, ci) => (
                            <td key={ci} className="px-4 py-2 text-mld-text-primary">
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
